import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";
import { LOADER } from "@/constants/testIds";

/**
 * Signature on-load moment.
 * A masked wordmark reveals from behind two shutters that split apart,
 * then the whole plate lifts off the screen.
 */
export default function SplashLoader() {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 2400);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid={LOADER.root}
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 1.15, ease: [0.76, 0, 0.24, 1] }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a0a0a] overflow-hidden"
        >
          {/* Grain */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-25 mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>\")",
            }}
          />

          {/* Top counter */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="absolute top-8 left-8 md:top-10 md:left-12 flex items-center gap-4"
          >
            <span className="block h-px w-8 md:w-14 bg-[#c9d3d8]/40" />
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#c9d3d8]/70">
              VISION · Loading
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="absolute top-8 right-8 md:top-10 md:right-12 text-right"
          >
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#c9d3d8]/70">
              Vol. I / MMXXIV
            </span>
          </motion.div>

          {/* Wordmark — masked reveal */}
          <div className="relative flex items-center justify-center">
            <div className="overflow-hidden">
              <motion.div
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                exit={{ y: "-110%" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                className="will-change-transform"
              >
                <Logo
                  className="h-20 md:h-32 w-72 md:w-[32rem]"
                  color="#e8ecef"
                />
              </motion.div>
            </div>
          </div>

          {/* Sub — Chapter kicker */}
          <div className="absolute bottom-24 md:bottom-32 left-0 right-0 flex justify-center">
            <div className="overflow-hidden">
              <motion.p
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                exit={{ y: "-110%" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
                className="text-[10px] md:text-xs tracking-[0.5em] uppercase text-[#c9d3d8]/80"
              >
                Chapter 00 — Arrival
              </motion.p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="absolute bottom-10 md:bottom-14 left-8 right-8 md:left-12 md:right-12 h-px bg-[#c9d3d8]/15 overflow-hidden">
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.1, ease: [0.22, 1, 0.36, 1] }}
              className="block h-full bg-[#e8ecef] origin-left"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
